<template>
  <section class="box coverage-summary" aria-labelledby="coverage-summary-title">
    <h2 id="coverage-summary-title" class="title is-4">Coverage summary</h2>
    <p v-if="!sections.length" class="has-text-grey">
      No plan-level deductible or out-of-pocket amounts returned for the selected filters.
    </p>
    <section v-for="section in sections" :key="section.key" class="coverage-level">
      <div class="coverage-level-heading">
        <h3 class="title is-6">{{ section.level || 'Level not specified' }}</h3>
        <span v-if="section.status" class="coverage-status">{{ section.status }}</span>
      </div>
      <div class="coverage-cards">
        <article v-for="card in section.cards" :key="card.key" class="coverage-card">
          <div class="coverage-card-heading">
            <h4>{{ card.benefit.amount || card.benefit.amount_code }}</h4>
            <span>{{ card.benefit.network || card.benefit.network_code || 'Network not specified' }}</span>
          </div>
          <p v-if="!section.status" class="coverage-status">{{ card.status }}</p>
          <p v-if="card.description" class="coverage-description">{{ card.description }}</p>
          <p v-if="card.context" class="coverage-description">{{ card.context }}</p>
          <template v-if="card.amounts.length">
            <div v-for="(amount, index) in card.amounts" :key="index" class="coverage-accumulation">
              <p v-if="amount.description" class="coverage-description">{{ amount.description }}</p>
              <p v-if="amount.terms" class="coverage-description">{{ amount.terms }}</p>
              <p class="coverage-value">{{ formatAmount(amount.remaining) }} <span>remaining</span></p>
              <p class="coverage-period">{{ amount.period }}</p>
              <progress
                v-if="amount.total > 0"
                class="progress is-primary"
                :value="amount.met"
                :max="amount.total"
                :aria-label="`${card.benefit.amount}: ${formatAmount(amount.met)} met of ${formatAmount(amount.total)}`"
              >{{ formatAmount(amount.met) }} met</progress>
              <div v-else class="coverage-progress-unavailable" role="img" aria-label="Progress percentage is not applicable for a zero total." />
              <div class="coverage-progress-label">
                <span>
                  {{ formatAmount(amount.met) }} met
                  <b-tooltip
                    class="coverage-met-tooltip"
                    label="Calculated as total minus remaining."
                    type="is-dark"
                    position="is-right"
                    size="is-small"
                    :triggers="['hover', 'focus', 'click']"
                    multilined
                  >
                    <button type="button" class="coverage-met-help" aria-label="Amount met: calculated as total minus remaining.">
                      <b-icon icon="information-outline" size="is-small" />
                    </button>
                  </b-tooltip>
                </span>
                <span>of {{ formatAmount(amount.total) }}</span>
              </div>
            </div>
          </template>
          <template v-else>
            <p v-if="!card.reported.length" class="coverage-fallback">Amount not reported.</p>
            <div v-for="(entry, index) in card.reported" :key="index">
              <p :class="entry.value ? 'coverage-value' : 'coverage-fallback'">
                {{ entry.value }} <span>{{ entry.label }}</span>
              </p>
              <p v-if="entry.period" class="coverage-period">{{ entry.period }}</p>
            </div>
            <div class="coverage-progress-unavailable" role="img" aria-label="Progress unavailable: a matching total and remaining amount could not be determined." />
            <div class="coverage-progress-label">
              <span>{{ card.reportedMet || '—' }} met</span>
              <span>{{ card.reportedTotal ? `of ${card.reportedTotal}` : 'Total —' }}</span>
            </div>
          </template>
        </article>
      </div>
    </section>
  </section>
</template>

<script>
export default {
  name: 'InquiryDetailCoverageSummary',
  props: {
    benefitGroups: { type: Array, default: () => [] },
    selectedNetworks: { type: Array, default: () => ['All'] },
    selectedPayments: { type: Array, default: () => ['All'] },
    selectedBenefits: { type: Array, default: () => ['All'] },
    selectedLevel: { type: String, default: 'All' },
  },
  computed: {
    sections() {
      const levels = new Map();
      for (const [groupIndex, group] of (this.benefitGroups || []).entries()) {
        if (group.name?.toLowerCase() !== 'health benefit plan coverage') continue;
        if (!this.selectedBenefits.includes('All') && !this.selectedBenefits.includes(group.name)) continue;
        for (const [index, benefit] of (group.benefits || []).entries()) {
          if (!['deductibles', 'outOfPocket'].includes(benefit.amount_code)) continue;
          if (!this.selectedNetworks.includes('All') && !this.selectedNetworks.includes(benefit.network_code)) continue;
          if (!this.selectedPayments.includes('All') && !this.selectedPayments.includes(benefit.amount_code)) continue;
          if (!['All', benefit.level].includes(this.selectedLevel)) continue;
          const level = benefit.level || '';
          const key = `${groupIndex}-${level}`;
          if (!levels.has(key)) levels.set(key, {
            key, level, groupIndex, cards: [],
          });
          const entries = benefit.merged_benefit_texts?.length
            ? benefit.merged_benefit_texts : [{ text: benefit.text, notes: benefit.payer_notes || [] }];
          let description = benefit.description;
          if (!description && new Set(entries.map((entry) => JSON.stringify(entry.notes || []))).size <= 1) {
            const descriptions = [...new Set(entries.flatMap((entry) => (entry.text || '').split(/,?\r?\n/)
              .flatMap((text) => {
                const match = text.trim().match(/ \((.+)\)$/);
                return match ? [match[1]] : [];
              })))];
            if (descriptions.length === 1) description = descriptions[0];
          }
          const parsed = this.parseAccumulation({ ...benefit, description });
          const amount = parsed && description !== benefit.description ? { ...parsed, description } : parsed;
          let amounts = amount ? [amount] : entries.flatMap((entry) => (entry.text || '').split(/,?\r?\n/)
            .map((text) => this.parseAccumulation({ ...benefit, merged_benefit_texts: [{ ...entry, text }] })));
          if (amounts.some((value) => !value)) amounts = [];
          if (!amounts.length) {
            const qualified = new Map();
            for (const entry of entries) {
              for (const text of (entry.text || '').split(/,?\r?\n/)) {
                const match = text.trim().match(/^\$[\d,.]+ (?:Individual|Family) (?:Remaining|Year to Date|Calendar Year|Service Year|Contract) (?:Deductible|Out of Pocket) \((.+)\)$/);
                if (!match) {
                  qualified.clear();
                  break;
                }
                if (!qualified.has(match[1])) qualified.set(match[1], []);
                qualified.get(match[1]).push({ ...entry, text });
              }
              if (!qualified.size) break;
            }
            const truncated = benefit.description?.includes('(') && !benefit.description.includes(')') &&
              [...qualified.keys()].every((description) => description.startsWith(`${benefit.description})`));
            if ((qualified.size > 1 && qualified.has(benefit.description)) || (qualified.size && truncated)) {
              const parsed = [...qualified].map(([description, texts]) => {
                const value = this.parseAccumulation({ ...benefit, description, merged_benefit_texts: texts });
                return value ? { ...value, description } : null;
              });
              if (parsed.every((value) => value)) amounts = parsed;
            }
          }
          if (!amounts.length) {
            const qualified = new Map();
            for (const entry of entries) {
              const key = JSON.stringify(entry.notes || []);
              if (!qualified.has(key)) qualified.set(key, []);
              qualified.get(key).push(entry);
            }
            if (qualified.size > 1) {
              const parsed = [...qualified.values()].map((texts) => {
                const value = this.parseAccumulation({ ...benefit, merged_benefit_texts: texts });
                const notes = (texts[0].notes || []).map((note) => typeof note === 'string' ? note : note.message)
                  .filter((note) => typeof note === 'string' && note.trim())
                  .filter((note) => !/^\s*\[[^\r\n]+\]\s*:\s*$/.test(note) &&
                    !/^(?:Address(?: \d)?|City|State|Zip|Information Contact|Work Phone Number|Phone|Fax|Federal TaxPayer's ID):/i.test(note))
                  .map((note) => note.replace(/^Last Name:\s*/i, '').trim());
                return value ? { ...value, terms: [...new Set(notes)].join(' · ') || 'Other reported limit' } : null;
              });
              if (parsed.every((value) => value)) amounts = parsed;
            }
          }
          const reported = amounts.length ? [] : entries.flatMap((entry) => (entry.text || '').split(/,?\r?\n/)
            .filter((text) => text.trim()).map((text) => {
              const match = text.trim().match(/^\$((?:\d{1,3}(?:,\d{3})+|\d+)(?:\.\d{1,2})?)(?:\s+(.*))?$/);
              if (!match) return { value: '', label: text.trim() };
              const value = Math.round(Number(match[1].replace(/,/g, '')) * 100);
              if (!Number.isSafeInteger(value)) return { value: '', label: text.trim() };
              let label = (match[2] || '').replace(/^(?:Individual|Family|Employee Only|Employee)\b\s*/, '')
                .replace(/\b(?:Deductible|Out of Pocket)\b/, '').replace(/^\/\s*/, '').trim();
              if (!label) {
                const notes = [...(entry.notes || []), ...(benefit.notes || []), ...(benefit.payer_notes || [])];
                const periods = [...new Set(notes.flatMap((note) =>
                  ((typeof note === 'string' ? note : note.message) || '').toLowerCase().match(/\bper (?:confinement|admission|visit|day)\b/g) || []))];
                label = periods.length === 1 ? periods[0] : 'reported amount';
              }
              const period = ['Calendar Year', 'Service Year', 'Contract'].includes(label) ? label : '';
              return { value: this.formatAmount(value), label: period ? 'total' : label === 'Remaining' ? 'remaining' : label, period };
            })).filter((entry, index, values) =>
            values.findIndex((existing) => existing.value === entry.value && existing.label === entry.label && existing.period === entry.period) === index
          );
          const totals = reported.filter((entry) => entry.label === 'total');
          const met = reported.filter((entry) => entry.label === 'Year to Date');
          const sameTerms = reported.length === 1 || new Set(entries.map((entry) => JSON.stringify(entry.notes || []))).size <= 1;
          levels.get(key).cards.push({
            key: `${groupIndex}-${index}`,
            groupIndex,
            benefit,
            description: amounts.some((value) => value.description) ? '' : benefit.description,
            status: group.status || [...new Set((group.status_details || []).map((detail) => detail.status).filter(Boolean))].join(' / ') || 'Coverage status not returned',
            amounts,
            reported,
            reportedTotal: sameTerms && totals.length === 1 ? totals[0].value : null,
            reportedMet: sameTerms && met.length === 1 ? met[0].value : null,
            context: [benefit.place_of_service, benefit.plan_network_name, benefit.plan_network_id,
              benefit.insurance_type, benefit.plan_number ? `Plan: ${benefit.plan_number}` : ''].filter(Boolean).join(' · '),
          });
        }
      }
      const order = { Individual: 0, Family: 1 };
      const payments = { deductibles: 0, outOfPocket: 1 };
      const networks = { inNetwork: 0, outOfNetwork: 1 };
      for (const section of levels.values()) {
        section.status = section.cards.every((card) => card.status === section.cards[0].status)
          ? section.cards[0].status : '';
        section.cards.sort((a, b) =>
          payments[a.benefit.amount_code] - payments[b.benefit.amount_code] ||
          (networks[a.benefit.network_code] ?? 2) - (networks[b.benefit.network_code] ?? 2)
        );
      }
      return Array.from(levels.values()).sort((a, b) =>
        a.groupIndex - b.groupIndex ||
        (order[a.level] ?? 2) - (order[b.level] ?? 2)
      );
    },
  },
  methods: {
    formatAmount(cents) {
      return (cents / 100).toLocaleString('en-US', { style: 'currency', currency: 'USD' });
    },
    parseAccumulation(benefit) {
      const entries = benefit.merged_benefit_texts?.length
        ? benefit.merged_benefit_texts
        : [{ text: benefit.text, notes: benefit.payer_notes || [] }];
      const groups = new Map();
      for (const entry of entries) {
        const key = JSON.stringify(entry.notes || []);
        if (!groups.has(key)) groups.set(key, []);
        groups.get(key).push(entry);
      }
      const lines = [];
      for (const entry of entries) {
        if (typeof entry.text !== 'string' || !entry.text.trim()) return null;
        for (const text of entry.text.split(/,?\r?\n/)) {
          const line = { text: text.trim(), notes: JSON.stringify(entry.notes || []) };
          if (!lines.some((existing) => existing.text === line.text && existing.notes === line.notes)) lines.push(line);
        }
      }
      const suffix = benefit.description ? ` (${benefit.description})` : '';
      if (suffix && lines.some((line) => line.text.endsWith(suffix))) {
        if (!lines.every((line) => line.text.endsWith(suffix)) && new Set(lines.map((line) => line.notes)).size > 1) return null;
        for (const line of lines) {
          if (line.text.endsWith(suffix)) line.text = line.text.slice(0, -suffix.length);
        }
      }
      if ([...groups.keys()].filter((key) => key !== '[]').length > 1) {
        const amounts = [...groups.values()].map((group) => this.parseAccumulation({ ...benefit, merged_benefit_texts: group }));
        return amounts.every((amount) => amount && amount.remaining === amounts[0]?.remaining &&
          amount.total === amounts[0].total && amount.period === amounts[0].period) ? amounts[0] : null;
      }
      const currency = '\\$((?:\\d{1,3}(?:,\\d{3})+|\\d+)(?:\\.\\d{1,2})?|\\.\\d{1,2})';
      const cents = (value) => Math.round(Number(value.replace(/,/g, '')) * 100);
      let remaining;
      let total;
      let period;
      if (lines.length === 1) {
        const match = lines[0].text.match(new RegExp(`^${currency} remaining of ${currency} / Year to Date(?: / (Calendar Year|Service Year|Contract))?$`));
        if (!match) return null;
        remaining = cents(match[1]);
        total = cents(match[2]);
        period = match[3] ? `Year to Date / ${match[3]}` : 'Year to Date';
      } else if ([2, 3].includes(lines.length) && new Set(lines.map((line) => line.notes).filter((notes) => notes !== '[]')).size <= 1) {
        const label = benefit.amount_code === 'deductibles' ? 'Deductible' : 'Out of Pocket';
        let reportedMet;
        for (const line of lines) {
          const match = (['Family', 'Individual'].includes(benefit.level) &&
            line.text.match(new RegExp(`^${currency} ${benefit.level} (Remaining|Year to Date|Calendar Year|Service Year|Contract) ${label}$`))) ||
            line.text.match(new RegExp(`^${currency} / (Year to Date|Calendar Year|Service Year|Contract)$`)) ||
            line.text.match(new RegExp(`^${currency} (remaining)$`));
          if (!match) return null;
          if (match[2].toLowerCase() === 'remaining') {
            if (remaining !== undefined) return null;
            remaining = cents(match[1]);
          } else if (match[2] === 'Year to Date') {
            if (reportedMet !== undefined) return null;
            reportedMet = cents(match[1]);
          } else {
            if (total !== undefined) return null;
            total = cents(match[1]);
            period = match[2];
          }
        }
        if (reportedMet !== undefined && (!Number.isSafeInteger(reportedMet) || reportedMet !== total - remaining)) return null;
      } else {
        return null;
      }
      if (!Number.isSafeInteger(remaining) || !Number.isSafeInteger(total) || remaining < 0 || remaining > total) return null;
      return { remaining, total, met: total - remaining, period };
    },
  },
};
</script>

<style scoped>
.coverage-summary { scroll-margin-top: 5rem; }
.coverage-level + .coverage-level { margin-top: 1.5rem; padding-top: 1.5rem; border-top: 1px solid #eee; }
.coverage-level-heading { display: flex; align-items: baseline; gap: 0.75rem; flex-wrap: wrap; margin-bottom: 0.75rem; }
.coverage-level-heading > .title { margin-bottom: 0; }
.coverage-cards { display: grid; grid-template-columns: repeat(2, minmax(0, 1fr)); gap: 0.75rem; }
.coverage-card { padding: 0.75rem; border: 1px solid #e8e8e8; border-radius: 5px; overflow-wrap: anywhere; }
.coverage-card-heading { display: flex; justify-content: space-between; align-items: baseline; gap: 0.5rem; flex-wrap: wrap; }
.coverage-card-heading h4 { font-size: 0.9rem; font-weight: 600; }
.coverage-card-heading > span { color: #7957d5; font-size: 0.8rem; }
.coverage-accumulation + .coverage-accumulation { margin-top: 1rem; }
.coverage-value { font-size: 1.4rem; color: #363636; font-weight: 600; font-variant-numeric: tabular-nums; margin-top: 0.5rem; }
.coverage-value > span { font-size: 0.8rem; font-weight: 400; color: #6b6b6b; }
.coverage-status { font-size: 0.75rem; color: #6b6b6b; margin-top: 0.25rem; }
.coverage-period, .coverage-description { font-size: 0.8rem; margin-top: 0.25rem; }
.progress, .coverage-progress-unavailable { height: 6px; margin: 0.5rem 0 0.375rem; }
.coverage-progress-unavailable { border-radius: 9999px; background: repeating-linear-gradient(135deg, #e8e8e8 0, #e8e8e8 4px, #f5f5f5 4px, #f5f5f5 8px); }
.coverage-progress-label { display: flex; justify-content: space-between; gap: 0.5rem; flex-wrap: wrap; font-size: 0.75rem; font-variant-numeric: tabular-nums; }
.coverage-met-tooltip { vertical-align: middle; }
.coverage-met-tooltip.is-multiline ::v-deep .tooltip-content { font-size: 0.75rem; line-height: 1.4; text-align: left; padding: 0.4rem 0.6rem; box-shadow: none; }
.coverage-met-help { display: inline-flex; align-items: center; justify-content: center; width: 1rem; height: 1rem; padding: 0; border: 0; background: transparent; color: #7a7a7a; cursor: help; }
.coverage-met-help:focus-visible { outline: 2px solid #7957d5; outline-offset: 2px; border-radius: 50%; }
.coverage-fallback { font-size: 0.85rem; margin-top: 0.5rem; }
@media (max-width: 600px) { .coverage-cards { grid-template-columns: 1fr; } }
</style>
